import { ReactNode } from 'react';
import {
  Button,
  Card,
  makeStyles,
  Subtitle1,
  Link as FluentLink,
  mergeClasses,
  Subtitle2Stronger,
} from '@fluentui/react-components';
import { Hamburger, NavDrawer as FluentNavDrawer, NavDrawerBody, NavDrawerHeader } from '@fluentui/react-nav-preview';
import classNames from 'classnames';
import {
  ArrowLeftFilled,
  PanelLeftContractFilled,
  PanelLeftContractRegular,
  PanelRightContractRegular,
  PanelRightContractFilled,
  bundleIcon,
} from '@fluentui/react-icons';
import { Link } from 'react-router-dom';

import { layoutTransition } from '@shared/constants/layoutTransition.constants';

const useStyles = makeStyles({
  drawer: {
    backgroundColor: 'transparent',
    // backgroundColor: '#f0f0f0',
    // borderRadius: '20px'
    // backgroundColor: tokens.colorNeutralBackground3,
    height: '100%',
  },
  drawerHeader: {
    padding: '0 5px 0 0',
    paddingInlineStart: '0',
  },
  card: {
    padding: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    gap: 0,
  },
  bodyCard: {
    padding: '0 !important',
  },
  bodyTransparent: {
    paddingTop: '0 !important',
  },
  block: {
    display: 'block',
  },
});

const PanelLeftContract = bundleIcon(PanelLeftContractFilled, PanelLeftContractRegular);
const PanelRightContract = bundleIcon(PanelRightContractFilled, PanelRightContractRegular);

export const NAV_DRAWER_COLLAPSED_WIDTH_PX = 52;

type TNavDrawer = {
  type: 'inline' | 'overlay';
  title: string;
  titleHref?: string;
  appearance?: 'transparent' | 'card';
  isOpen: boolean;
  setIsOpen: (state: boolean) => void;
  children: ReactNode;
  hrefBack?: string;
  selectedValue?: string;
  withPanelContractIcon?: boolean;
  width?: string;
  footer?: ReactNode;
};
const NavDrawer = ({
  type,
  title,
  titleHref,
  isOpen,
  setIsOpen,
  children,
  hrefBack,
  appearance = 'transparent',
  selectedValue = '1',
  withPanelContractIcon = true,
  width = '260px',
  footer,
}: TNavDrawer) => {
  const classes = useStyles();

  // className={classNames({ "border-b border-neutral-300": appearance === 'card' })}
  // backHref
  //           <Hamburger appearance="subtle" onClick={() => setIsOpen(false)} />

  const titleComponent =
    appearance === 'transparent' ? (
      <Subtitle2Stronger className={classNames('force-break-word ml-5', classes.block)}>{title}</Subtitle2Stronger>
    ) : appearance === 'card' ? (
      <Subtitle1 className={classNames('force-break-word ml-2 mb-1 block', classes.block)}>{title}</Subtitle1>
    ) : null;

  const content = (
    <>
      <NavDrawerHeader className={classes.drawerHeader}>
        <div
          className={classNames('flex justify-between items-center w-full', {
            'mt-2 pr-2': appearance === 'card',
            'mt-[6px] mb-[5px] pr-1': appearance === 'transparent',
          })}
        >
          <div className="flex">
            {hrefBack && (
              <Link to={hrefBack} className="ml-2">
                <Button icon={<ArrowLeftFilled />} appearance="subtle" />
              </Link>
            )}
            {/* --- title --- */}
            {titleHref ? (
              <Link to={titleHref}>
                <FluentLink appearance="subtle">{titleComponent}</FluentLink>
              </Link>
            ) : (
              titleComponent
            )}
          </div>
          {withPanelContractIcon && (
            <div
              /* hidden on desktop until the whole drawer is hovered */
              className="opacity-100 pointer-events-auto transition-opacity duration-200
                         lg:opacity-0 lg:pointer-events-none
                         lg:group-hover:opacity-100 lg:group-hover:pointer-events-auto"
            >
              <Button
                icon={<PanelLeftContract />}
                size="large"
                appearance="transparent"
                onClick={() => setIsOpen(false)}
              />
            </div>
          )}
        </div>
      </NavDrawerHeader>

      {appearance === 'card' ? (
        <NavDrawerBody className={appearance === 'card' ? classes.bodyCard : ''}>{children}</NavDrawerBody>
      ) : (
        <div className={footer ? 'relative flex-1 min-h-0' : 'relative h-full'}>
          <div className="absolute top-0 left-0 h-full overflow-y-auto w-full">
            <div>
              <NavDrawerBody className={classes.bodyTransparent}>{children}</NavDrawerBody>
            </div>
          </div>
        </div>
      )}
      {/* Not Fluent's NavDrawerFooter - Fluent 2 advises against it. */}
      {footer && <div className="pl-5 pr-2 pt-2 pb-4">{footer}</div>}
    </>
  );

  const isInSetup = ['reference-detection', 'library-detection', 'tags'].includes(selectedValue);

  return (
    <div
      className={classNames('flex align-top', {
        'mr-2': appearance === 'transparent',
        'mt-1': appearance === 'card',
      })}
    >
      {withPanelContractIcon && (
        <div
          className="overflow-hidden shrink-0"
          style={{ width: isOpen ? 0 : NAV_DRAWER_COLLAPSED_WIDTH_PX, transition: layoutTransition('width') }}
        >
          <Button
            icon={<PanelRightContract />}
            size="large"
            appearance="transparent"
            onClick={() => setIsOpen(true)}
            className={classNames('!ml-3', {
              '!mt-3': appearance === 'card',
              '!mt-[10px]': appearance === 'transparent',
            })}
          />
        </div>
      )}
      {/* Fluent's exit motion is a transform, so without this the drawer's layout space collapses in one frame at unmount. */}
      <div
        className="h-full overflow-hidden"
        style={{ width: isOpen ? width : '0px', transition: layoutTransition('width') }}
      >
        <FluentNavDrawer
          selectedValue={selectedValue}
          defaultOpenCategories={isInSetup ? ['setup'] : []} // <- opens it only on mount
          /* keep the category header visually selected even when user collapses it */
          selectedCategoryValue={isInSetup ? 'setup' : undefined}
          type={type}
          open={isOpen}
          style={{ width }}
          className={mergeClasses('group', classes.drawer)} /* group enables hover targeting */
        >
          {appearance === 'card' ? (
            <Card className={classNames('h-full mt-1 mr-1', classes.card)} size="large">
              {content}
            </Card>
          ) : (
            <div className={classNames('h-full mt-1 mr-1', { 'flex flex-col': footer })}>{content}</div>
          )}
        </FluentNavDrawer>
      </div>
    </div>
  );
};

export { NavDrawer };
